import React, { useRef } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { ApplicationStatus, KanbanColumn, ApplicationSummary } from '../../types';
import BoardColumn from './BoardColumn';
import { COLUMN_STATI } from './kanbanConstants';

interface Props {
  columns: KanbanColumn[];
  setColumns: React.Dispatch<React.SetStateAction<KanbanColumn[]>>;
  onStatusChange: (applicationId: number, status: ApplicationStatus) => void;
  onCardClick: (application: ApplicationSummary) => void;
  onArchive: (application: ApplicationSummary) => void;
  onQuickAdd: (status: ApplicationStatus) => void;
}

const Board: React.FC<Props> = ({ columns, setColumns, onStatusChange, onCardClick, onArchive, onQuickAdd }) => {
  const dragStartColumn = useRef<ApplicationStatus | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const findCard = (id: string): { column: KanbanColumn; index: number } | null => {
    for (const column of columns) {
      const index = column.applications.findIndex((a) => `card:${a.id}` === id);
      if (index >= 0) return { column, index };
    }
    return null;
  };

  const resolveColumn = (id: string): KanbanColumn | undefined => {
    if ((COLUMN_STATI as string[]).includes(id)) {
      return columns.find((c) => c.status === id);
    }
    const pos = findCard(id);
    return pos ? pos.column : undefined;
  };

  const handleDragStart = (event: DragStartEvent) => {
    const pos = findCard(String(event.active.id));
    dragStartColumn.current = pos ? pos.column.status : null;
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;
    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);
    const activePos = findCard(activeIdStr);
    if (!activePos) return;
    const overColumn = resolveColumn(overIdStr);
    if (!overColumn) return;
    const overPos = findCard(overIdStr);
    const fromIdx = columns.findIndex((c) => c.status === activePos.column.status);
    const toIdx = columns.findIndex((c) => c.status === overColumn.status);

    if (fromIdx === toIdx) {
      if (!overPos || activePos.index === overPos.index) return;
      setColumns((prev) => {
        const col = prev[fromIdx];
        const apps = arrayMove([...col.applications], activePos.index, overPos.index);
        return prev.map((c, i) => (i === fromIdx ? { ...c, applications: apps } : c));
      });
    } else {
      setColumns((prev) => {
        const fromCol = prev[fromIdx];
        const toCol = prev[toIdx];
        const card = fromCol.applications[activePos.index];
        if (!card) return prev;
        const fromApps = fromCol.applications.filter((_, i) => i !== activePos.index);
        const toApps = [...toCol.applications];
        const insertAt = overPos ? overPos.index : toApps.length;
        toApps.splice(insertAt, 0, card);
        return prev.map((c, i) => {
          if (i === fromIdx) return { ...c, applications: fromApps };
          if (i === toIdx) return { ...c, applications: toApps };
          return c;
        });
      });
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const fromStatus = dragStartColumn.current;
    dragStartColumn.current = null;
    if (!over) return;
    const activePos = findCard(String(active.id));
    if (!activePos) return;
    const overColumn = resolveColumn(String(over.id));
    if (!overColumn) return;
    if (fromStatus && fromStatus !== activePos.column.status) {
      onStatusChange(activePos.column.applications[activePos.index].id, activePos.column.status);
    }
  };

  const handleDragCancel = () => {
    dragStartColumn.current = null;
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex gap-4 overflow-x-auto pb-2">
        {columns.map((column) => (
          <BoardColumn
            key={column.status}
            status={column.status}
            label={column.label}
            applications={column.applications}
            onCardClick={onCardClick}
            onArchive={onArchive}
            onQuickAdd={() => onQuickAdd(column.status)}
          />
        ))}
      </div>
    </DndContext>
  );
};

export default Board;